import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import { Contractor, TimeEntry } from '../../../types';
import api from '../../../services/api';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

const HourRateManagementTab: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [subTabValue, setSubTabValue] = useState(0);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [newRate, setNewRate] = useState('');
  const [viewPeriod, setViewPeriod] = useState<'week' | 'month'>('month');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractorsRes, timeEntriesRes] = await Promise.all([
        api.get('/contractors'),
        api.get('/time-entries?status=PENDING'),
      ]);
      setContractors(contractorsRes.data || []);
      setTimeEntries(timeEntriesRes.data || []);
    } catch (error: any) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRateDialog = (contractor: Contractor) => {
    setSelectedContractor(contractor);
    setNewRate(contractor.hourlyRate.toString());
    setRateDialogOpen(true);
  };

  const handleCloseRateDialog = () => {
    setRateDialogOpen(false);
    setSelectedContractor(null);
    setNewRate('');
  };

  const handleUpdateRate = async () => {
    if (!selectedContractor) return;

    try {
      await api.put(`/contractors/${selectedContractor.id}`, {
        hourlyRate: parseFloat(newRate),
      });
      fetchData();
      handleCloseRateDialog();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update rate');
    }
  };

  const handleApproveHours = async (entryId: string) => {
    try {
      await api.put(`/time-entries/${entryId}`, { status: 'APPROVED' });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to approve hours');
    }
  };

  const handleRejectHours = async (entryId: string) => {
    try {
      await api.put(`/time-entries/${entryId}`, { status: 'REJECTED' });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to reject hours');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={subTabValue} onChange={(_, v) => setSubTabValue(v)}>
          <Tab label="Set Hourly Rates" />
          <Tab label="Review Submitted Hours" />
          <Tab label="Timesheet Summaries" />
        </Tabs>
      </Paper>

      <TabPanel value={subTabValue} index={0}>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Contractor Hourly Rates
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contractor Name</TableCell>
                  <TableCell>Current Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contractors.map((contractor) => (
                  <TableRow key={contractor.id}>
                    <TableCell>{contractor.name}</TableCell>
                    <TableCell>${contractor.hourlyRate.toFixed(2)}/hr</TableCell>
                    <TableCell>
                      <Chip
                        label={contractor.isActive ? 'Active' : 'Inactive'}
                        color={contractor.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Rate">
                        <IconButton size="small" onClick={() => handleOpenRateDialog(contractor)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rate History">
                        <IconButton size="small">
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Alert severity="info">
          Rate change history tracking will be available in a future update.
        </Alert>
      </TabPanel>

      <TabPanel value={subTabValue} index={1}>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Pending Hours Review
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contractor</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No pending hours to review</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  timeEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{entry.contractor?.name || '-'}</TableCell>
                      <TableCell>{entry.project?.name || '-'}</TableCell>
                      <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{entry.hoursWorked}</TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveHours(entry.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectHours(entry.id)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      <TabPanel value={subTabValue} index={2}>
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Timesheet Summaries</Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>View Period</InputLabel>
              <Select
                value={viewPeriod}
                label="View Period"
                onChange={(e) => setViewPeriod(e.target.value as 'week' | 'month')}
              >
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Alert severity="info">
            Timesheet summary view will display weekly/monthly contractor activity. Full implementation coming soon.
          </Alert>
        </Box>
      </TabPanel>

      <Dialog open={rateDialogOpen} onClose={handleCloseRateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Hourly Rate</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Contractor: {selectedContractor?.name}
            </Typography>
            <TextField
              label="New Hourly Rate *"
              type="number"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0' }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRateDialog}>Cancel</Button>
          <Button onClick={handleUpdateRate} variant="contained" disabled={!newRate}>
            Update Rate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HourRateManagementTab;

